import { useState, useRef, useMemo } from 'react';
import { useGetPartDefinitionsQuery } from '../../../api/endpoints/dealerPartsCatalog';
import { useLanguage } from '../../../contexts/LanguageContext';

export const usePartsCatalog = () => {
    const { th } = useLanguage();
    const toast = useRef(null);
    const [filters, setFilters] = useState({
        partCode: '',
        languageId: 1,
        pageNumber: 1,
        pageSize: 10
    });
    const [showFilters, setShowFilters] = useState(true);
    const [first, setFirst] = useState(0);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);

    const { data: partsData = {}, isLoading: isLoadingParts, refetch } = useGetPartDefinitionsQuery(filters);

    const rawPartsList = partsData?.data?.items || [];
    const totalRecords = partsData?.data?.totalCount || 0;

    const partsList = useMemo(() => {
        if (!sortField || !sortOrder) return rawPartsList;

        return [...rawPartsList].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];

            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;

            if (typeof aVal === 'string') {
                return sortOrder === 1 ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }

            if (typeof aVal === 'number') {
                return sortOrder === 1 ? aVal - bVal : bVal - aVal;
            }

            return 0;
        });
    }, [rawPartsList, sortField, sortOrder]);

    const handleSearch = () => {
        setFirst(0);
        refetch();
    };

    const searchByPartCode = (partCode) => {
        setFirst(0);
        setFilters(prev => ({
            ...prev,
            partCode,
            pageNumber: 1
        }));
    };

    const handleReset = () => {
        setFirst(0);
        setSortField(null);
        setSortOrder(null);
        setFilters({
            partCode: '',
            languageId: 1,
            pageNumber: 1,
            pageSize: 10
        });
    };

    const onPageChange = (event) => {
        const newPageNumber = Math.floor(event.first / event.rows) + 1;
        setFirst(event.first);
        setFilters((prevFilters) => ({
            ...prevFilters,
            pageSize: event.rows,
            pageNumber: newPageNumber
        }));
    };

    const onSort = (event) => {
        setSortField(event.sortField);
        setSortOrder(event.sortOrder);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString(th('common.locale'));
    };

    const statusBodyTemplate = (rowData) => {
        return rowData.isActive === 1 ? th('common.active') : th('common.inactive');
    };

    return {
        toast,
        filters,
        setFilters,
        showFilters,
        setShowFilters,
        partsList,
        totalRecords,
        isLoadingParts,
        handleSearch,
        searchByPartCode,
        handleReset,
        formatDate,
        statusBodyTemplate,
        first,
        onPageChange,
        onSort,
        sortField,
        sortOrder,
        th
    };
};
